// RecentActivity — redesigned (EM handoff): clean white card with a status
// timeline. Wired to the same `logs` data.
import React from 'react';
import PropTypes from 'prop-types';
import { Activity, CheckCircle2, Clock, XCircle, Plane, Star, AlertTriangle } from 'lucide-react';
import { T } from '../../components/em/ui';
import { displayStatus, NOT_MARKED_OUT } from './attendanceStatus';
import { formatDuration, formatHoursDuration } from '../../utils/duration';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' });
};

const STATUS_META = {
  present: { label: 'Present', color: T.success600, bg: T.success50, Icon: CheckCircle2 },
  'in-progress': { label: 'In Progress', color: T.info600, bg: T.info50, Icon: Clock },
  [NOT_MARKED_OUT]: { label: 'Not marked out', color: '#b45309', bg: T.warn50, Icon: AlertTriangle },
  leave: { label: 'Leave', color: T.purple, bg: T.purple50, Icon: Plane },
  absent: { label: 'Absent', color: T.danger, bg: T.danger50, Icon: XCircle },
  holiday: { label: 'Holiday', color: T.orange, bg: T.orange50, Icon: Star },
};

const RecentActivity = ({ logs = [] }) => {
  const list = (logs || []).slice(0, 8);
  return (
    <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 20px', borderBottom: `1px solid ${T.divider}` }}>
        <Activity size={16} color={T.orange} />
        <h3 style={{ margin: 0, font: `700 14px ${T.font}`, color: T.fg1 }}>Recent Activity</h3>
      </div>

      <div style={{ padding: '14px 20px' }}>
        {list.length === 0 ? (
          <div style={{ font: `400 13px ${T.font}`, color: T.fg7, textAlign: 'center', padding: '20px 0' }}>No attendance records for this month</div>
        ) : (
          list.map((l, i) => {
            const status = displayStatus(l);
            const meta = STATUS_META[status] || { label: status || 'Unknown', color: T.fg5, bg: T.bgMuted, Icon: Clock };
            const inTime = fmtTime(l.mark_in);
            const outTime = fmtTime(l.mark_out);
            let summary = '';
            if (inTime && outTime) {
              summary = `In ${inTime} · Out ${outTime}`;
              // `!= null`, not truthiness — a genuine "0.00" hour day still
              // deserves its figure rather than a bare in/out line.
              //
              // worked_seconds is exact; total_hours is rounded to 2dp (36s
              // granularity), so multiplying it back out reported a 1m07s day
              // as "1m 12s". Fall back to it only for payloads that predate
              // the field.
              if (l.worked_seconds != null) summary += ` · ${formatDuration(l.worked_seconds)}`;
              else if (l.total_hours != null) summary += ` · ${formatHoursDuration(l.total_hours)}`;
            } else if (inTime) summary = `Marked in at ${inTime}`;
            else summary = 'No check-in recorded';
            const last = i === list.length - 1;
            return (
              <div key={i} style={{ display: 'flex', gap: 12, position: 'relative' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 9999, background: meta.bg, border: `1px solid ${meta.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <meta.Icon size={14} color={meta.color} />
                  </div>
                  {!last && <div style={{ flex: 1, width: 2, background: T.divider, margin: '2px 0' }} />}
                </div>
                <div style={{ flex: 1, paddingBottom: last ? 0 : 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ font: `600 12.5px ${T.font}`, color: T.fg1, flex: 1 }}>{fmtDate(l.date)}</span>
                    <span style={{ padding: '2px 9px', borderRadius: 9999, background: meta.bg, color: meta.color, font: `600 10px ${T.font}` }}>{meta.label}</span>
                  </div>
                  <div style={{ font: `400 11.5px ${T.font}`, color: T.fg5 }}>{summary}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

RecentActivity.propTypes = { logs: PropTypes.array };
export default RecentActivity;
